import {ConversationMessage} from '@stripe-internal/conversations-client';
import {
  ConversationsApiDataMessagePartChannelMenuSubmission,
  ConversationsApiDataMessagePartContentChannelMenuSubmissionPartContent,
  ConversationsApiDataMessagePartContentChannelMenuSubmissionPartContentEmailForm,
  ConversationsApiDataMessagePartContentChannelMenuSubmissionPartContentRacForm,
} from '../../generated/types';
import {
  asRecord,
  asString,
  asStringArray,
  firstStringValue,
  optionalString,
} from '../../utils';

export type ConversationsApiChannelMenuSubmissionPartWithoutId = Omit<
  ConversationsApiDataMessagePartChannelMenuSubmission,
  'id'
>;

const CHANNEL_MENU_SUBMISSION_CHANNELS = new Set<string>([
  'channel_chat',
  'channel_restart',
  'channel_rac',
  'channel_email',
]);

type ChannelMenuSubmissionChannel =
  | 'channel_chat'
  | 'channel_restart'
  | 'channel_rac'
  | 'channel_email';

const INLINE_SELECTION_CHANNELS = new Set<string>([
  'channel_chat',
  'channel_restart',
]);

function isChannelMenuSubmissionChannel(
  value: unknown,
): value is ChannelMenuSubmissionChannel {
  return (
    typeof value === 'string' && CHANNEL_MENU_SUBMISSION_CHANNELS.has(value)
  );
}

function isInlineSelectionChannel(
  value: string,
): value is 'channel_chat' | 'channel_restart' {
  return INLINE_SELECTION_CHANNELS.has(value);
}

function parseStrictRacForm(
  raw: unknown,
):
  | ConversationsApiDataMessagePartContentChannelMenuSubmissionPartContentRacForm
  | undefined {
  const form = asRecord(raw);
  if (!form) {
    return undefined;
  }

  if (
    typeof form.name !== 'string' ||
    typeof form.phone_number !== 'string' ||
    typeof form.message !== 'string'
  ) {
    return undefined;
  }

  return {
    name: form.name,
    phone_number: form.phone_number,
    message: form.message,
  };
}

function buildRacFormFromPii(
  record: Record<string, unknown>,
): ConversationsApiDataMessagePartContentChannelMenuSubmissionPartContentRacForm {
  return {
    name: asString(record.name),
    phone_number: asString(record.phone_number),
    message: firstStringValue(record, 'message', 'description'),
  };
}

// Flat data_with_pii and nested email_form objects share the same field layout.
function buildEmailFormFields(
  form: Record<string, unknown>,
): ConversationsApiDataMessagePartContentChannelMenuSubmissionPartContentEmailForm {
  const ccEmails = asStringArray(form.cc_emails);
  const attachmentIds = asStringArray(form.attachment_ids);
  const requestIssueContext = asRecord(form.request_issue_context);

  return {
    email: asString(form.email),
    body: asString(form.body),
    ...optionalString(form, 'subject'),
    ...(ccEmails ? {cc_emails: ccEmails} : {}),
    ...(attachmentIds ? {attachment_ids: attachmentIds} : {}),
    ...(requestIssueContext
      ? {request_issue_context: requestIssueContext}
      : {}),
  };
}

function parseEmailFormFields(
  raw: unknown,
):
  | ConversationsApiDataMessagePartContentChannelMenuSubmissionPartContentEmailForm
  | undefined {
  const form = asRecord(raw);
  if (!form) {
    return undefined;
  }

  return buildEmailFormFields(form);
}

function parseSubmissionContent(
  raw: unknown,
):
  | ConversationsApiDataMessagePartContentChannelMenuSubmissionPartContent
  | undefined {
  const content = asRecord(raw);
  if (!content || typeof content.text !== 'string') {
    return undefined;
  }
  if (!isChannelMenuSubmissionChannel(content.channel)) {
    return undefined;
  }

  const base: ConversationsApiDataMessagePartContentChannelMenuSubmissionPartContent =
    {
      text: content.text,
      channel: content.channel,
      ...optionalString(content, 'analytics_event_id'),
    };

  switch (content.channel) {
    case 'channel_chat':
    case 'channel_restart':
      return base;
    case 'channel_rac': {
      const racForm = parseStrictRacForm(content.rac_form);
      return racForm ? {...base, rac_form: racForm} : base;
    }
    case 'channel_email': {
      const emailForm = parseEmailFormFields(content.email_form);
      return emailForm ? {...base, email_form: emailForm} : base;
    }
    default:
      return undefined;
  }
}

export function parseMessagePartSubmission(
  raw: unknown,
): ConversationsApiChannelMenuSubmissionPartWithoutId | undefined {
  const submission = asRecord(raw);
  if (!submission || submission.type !== 'channel_menu_submission') {
    return undefined;
  }

  const parsedContent = parseSubmissionContent(submission.content);
  if (!parsedContent) {
    return undefined;
  }

  return {
    type: 'channel_menu_submission',
    content: parsedContent,
  };
}

function isMessagePartSubmission(message: ConversationMessage): boolean {
  return typeof message.data?.creator_part_id === 'string';
}

function resolveSubmissionText(
  message: ConversationMessage,
  fallback: string,
): string {
  if (message.type === 'text-conversation-message' && message.text.length > 0) {
    return message.text;
  }
  return fallback;
}

function resolveSelectionText(
  message: ConversationMessage,
  channel: string,
): string {
  return message.type === 'text-conversation-message' ? message.text : channel;
}

function buildEmailSubmissionText(
  dataWithPii: Record<string, unknown>,
): string {
  const lines: string[] = [];
  const email = asString(dataWithPii.email);
  if (email.length > 0) {
    lines.push(email);
  }
  const subject = asString(dataWithPii.subject);
  if (subject.length > 0) {
    lines.push(subject);
  }
  const ccEmails = asStringArray(dataWithPii.cc_emails);
  if (ccEmails && ccEmails.length > 0) {
    lines.push(`CC: ${ccEmails.join(', ')}`);
  }
  lines.push(asString(dataWithPii.body));
  return lines.join('\n');
}

function buildLegacyChannelMenuSubmission(
  message: ConversationMessage,
): ConversationsApiChannelMenuSubmissionPartWithoutId | undefined {
  const submissionData = asRecord(message.data?.data_with_pii);
  const inlineDataOutcome = asString(message.data?.inline_data_outcome);

  if (submissionData?.type === 'rac_form') {
    const racForm = buildRacFormFromPii(submissionData);
    const fallbackText = [
      racForm.name,
      racForm.phone_number,
      racForm.message,
    ].join('\n');

    return {
      type: 'channel_menu_submission',
      content: {
        text: resolveSubmissionText(message, fallbackText),
        channel: 'channel_rac',
        rac_form: racForm,
      },
    };
  }

  if (submissionData?.type === 'email_form') {
    return {
      type: 'channel_menu_submission',
      content: {
        text: resolveSubmissionText(
          message,
          buildEmailSubmissionText(submissionData),
        ),
        channel: 'channel_email',
        email_form: buildEmailFormFields(submissionData),
      },
    };
  }

  let channel: string | undefined;
  if (inlineDataOutcome && isInlineSelectionChannel(inlineDataOutcome)) {
    channel = inlineDataOutcome;
  } else if (
    submissionData &&
    typeof submissionData.selected_option_id === 'string'
  ) {
    channel = submissionData.selected_option_id;
  }

  if (channel && isInlineSelectionChannel(channel)) {
    return {
      type: 'channel_menu_submission',
      content: {
        text: resolveSelectionText(message, channel),
        channel,
      },
    };
  }

  return undefined;
}

export function buildChannelMenuSubmissionPart(
  message: ConversationMessage,
): ConversationsApiChannelMenuSubmissionPartWithoutId | undefined {
  return (
    parseMessagePartSubmission(message.data?.message_part_submission) ??
    (isMessagePartSubmission(message)
      ? buildLegacyChannelMenuSubmission(message)
      : undefined)
  );
}

export function isChannelMenuSubmission(message: ConversationMessage): boolean {
  return buildChannelMenuSubmissionPart(message) !== undefined;
}
